import type { BoardSnapshot, WorkItem, WorkStatus } from "@business-ops/domain";

export const ACTIVE_STATUSES: readonly WorkStatus[] = [
  "BACKLOG",
  "READY",
  "IN_PROGRESS",
  "INTERNAL_REVIEW",
  "CLIENT_REVIEW",
  "BLOCKED",
  "DONE"
];

export const STATUS_LABELS: Readonly<Record<WorkStatus, string>> = {
  BACKLOG: "Backlog",
  READY: "Ready",
  IN_PROGRESS: "In progress",
  INTERNAL_REVIEW: "Internal review",
  CLIENT_REVIEW: "Client review",
  BLOCKED: "Blocked",
  DONE: "Done",
  ARCHIVED: "Archived"
};

export function clientName(board: BoardSnapshot, item: WorkItem): string {
  return board.clients.find((client) => client.id === item.clientId)?.name ?? "No client";
}

export function clientColor(board: BoardSnapshot, item: WorkItem): string {
  return board.clients.find((client) => client.id === item.clientId)?.color ?? "#797b70";
}

export function memberName(board: BoardSnapshot, memberId: string | null): string {
  const member = board.members.find((candidate) => candidate.id === memberId);
  return member ? memberLabel(board.members, member) : "Unassigned";
}

export function memberLabel(
  members: BoardSnapshot["members"],
  member: BoardSnapshot["members"][number]
): string {
  const displayName = member.displayName.trim().replace(/\s+/g, " ");
  const normalized = displayName.toLocaleLowerCase();
  const duplicate = members.some((candidate) => candidate.id !== member.id
    && candidate.displayName.trim().replace(/\s+/g, " ").toLocaleLowerCase() === normalized);
  if (!duplicate) return displayName;
  // WHY [BOL-DUPLICATE-PERSON-LABEL-001]: imported staff records and login
  // accounts can legitimately share a display name. Exact routing cannot ask
  // an owner to choose or approve two visually identical people, while adding
  // roles to every unique name would create needless UI noise. Falsifier: any
  // selector or Send review shows duplicate people with indistinguishable text.
  const role = ({ OWNER: "Owner", MANAGER: "Manager", CONTRIBUTOR: "Contributor", VIEWER: "Viewer" } as const)[member.role];
  return `${displayName} · ${role}`;
}

export function projectName(board: BoardSnapshot, item: WorkItem): string | null {
  return board.projects.find((project) => project.id === item.projectId)?.name ?? null;
}

export function dueState(item: WorkItem, now = Date.now()): "OVERDUE" | "TODAY" | "SOON" | "LATER" | "NONE" {
  if (!item.dueAt || item.status === "DONE" || item.status === "ARCHIVED") return "NONE";
  const due = new Date(item.dueAt).getTime();
  const today = new Date(now);
  const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1).getTime();
  if (due < now) return "OVERDUE";
  if (due < endOfToday) return "TODAY";
  if (due <= now + 2 * 86_400_000) return "SOON";
  return "LATER";
}

export function formatDeadline(iso: string | null, options: { short?: boolean } = {}): string {
  if (!iso) return "No deadline";
  const date = new Date(iso);
  return new Intl.DateTimeFormat(undefined, options.short
    ? { month: "short", day: "numeric" }
    : { weekday: "short", month: "short", day: "numeric" }
  ).format(date);
}

export function formatTimeAgo(iso: string): string {
  const difference = Date.now() - new Date(iso).getTime();
  const minutes = Math.max(0, Math.floor(difference / 60_000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function toLocalDateTime(iso: string | null | undefined, timezone?: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (timezone) {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hourCycle: "h23"
    }).formatToParts(date);
    const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? "";
    return `${value("year")}-${value("month")}-${value("day")}T${value("hour")}:${value("minute")}`;
  }
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

export function fromLocalDateTime(localDateTime: string, timezone: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/u.exec(localDateTime);
  if (!match) throw new Error("Choose one exact date and time.");
  const [year, month, day, hour, minute] = match.slice(1).map(Number);
  const target = Date.UTC(year!, month! - 1, day!, hour!, minute!);
  let candidate = target;
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23"
  });
  const read = (value: number) => {
    const parts = formatter.formatToParts(new Date(value));
    const number = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)?.value);
    return { year: number("year"), month: number("month"), day: number("day"), hour: number("hour"), minute: number("minute"), second: number("second") };
  };
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const observed = read(candidate);
    const correction = target - Date.UTC(observed.year, observed.month - 1, observed.day, observed.hour, observed.minute, observed.second);
    candidate += correction;
    if (correction === 0) break;
  }
  const observed = read(candidate);
  if (observed.year !== year || observed.month !== month || observed.day !== day || observed.hour !== hour || observed.minute !== minute) {
    throw new Error("That time does not exist in the workspace timezone.");
  }
  // WHY [BOL-WORKSPACE-WALL-TIME-001]: Calendar inputs are business wall
  // times, not the reviewer's browser timezone. Preserve the workspace instant
  // through create/edit/approval; never use new Date(datetime-local) here.
  // Falsifier: the same 10:00 entry moves when reviewer and workspace differ.
  return new Date(candidate).toISOString();
}

export function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "").join("");
}
